// The founding form of CLAN › START ONE (R2155, ui-map-interface.md §16.11).
//
// A clan flies one faction for its whole life (R240), and the founder picks
// it in START ONE. The pick cannot be undone, so the form shows each faction
// whole, and it shows the picked one as the map will draw it:
//
//   - one FACTION CARD for each faction of the roster: the badge of the
//     faction mark at a large size, the full faction name (it wraps, and it
//     is never cut), the faction ink and the edge dash of its border. The
//     badge and the dash are the faction second channel (§2.2, R1568), so a
//     reader who cannot tell two inks apart still tells the cards apart.
//     The cards are a radio group: `role="radio"`, one tab stop on the
//     picked card, and the walk keys of `[keys.hub]` move the pick.
//   - the FOUNDING CHART: a patch of the sea chart, the cells of uncharted
//     water round one hold. A picked faction fills a hex ring round the hold
//     with the territory tint, strokes it with the three border passes and
//     the edge dash, and stands its badge beside the hold, as another
//     faction sees a hold of the clan (§6.2 card 01).
//
// The pictures come from the map's own descriptions: `drawFactionBadge`,
// `drawHoldMark` and the dash table, through the SVG surface of
// `render/svg-marks.ts`, and the cells from the topology of `world.ts`. No
// colour stands here: each ink is the faction colour of the roster, and the
// alphas are `[presentation.territory]`. The faction count is data.
//
// The builders are pure and return markup; `wireFactionWalk` adds the key
// walk to the radio group once.

import { HUB_FOUND, KEYS, FACTION_MARKS, TERRITORY_BORDER_PASSES, TERRITORY_TINT_ALPHA } from '../../../gen/presentation';
import { channels } from '../../../render/color';
import { hubClan as S } from '../../../gen/strings';
import { matchKey } from '../../../keys';
import { dashPattern, drawFactionBadge, siteBadgeAt } from '../../../render/faction-marks';
import { DEFAULT_HOLD_SYMBOL, drawHoldMark } from '../../../render/hold-marks';
import { outlineLoops } from '../../../render/outlines';
import { SvgPathMarks } from '../../../render/svg-marks';
import {
  COL_PITCH, FACTIONS, HEX_SIZE, cellCenterX, cellCenterY, factionColor, factionName, factionOf, hex, hexCorner,
  neighborAt,
} from '../../../world';
import { esc, lede, wireOnce } from '../panel';

/** The act of a faction card: `data-id` is the faction id. */
export const FACTION_ACT = 'clan-faction';

/** A figure of the markup: two decimals, and no "-0". */
function f2(v: number): string {
  const r = Math.round(v * 100) / 100;
  return String(r === 0 ? 0 : r);
}

// --- the faction card ----------------------------------------------------------

const badges = new Map<number, string>();

/**
 * The badge of one faction for its faction card: the map's own badge
 * (`drawFactionBadge`, the modern reading the hub wears) drawn at
 * `badgeDrawPx` and shown at `badgePx`, so the rim and the charge grow
 * with it. '' for a faction the roster does not hold.
 */
function cardBadgeSvg(faction: number): string {
  const hit = badges.get(faction);
  if (hit !== undefined) return hit;
  const f = factionOf(faction);
  let out = '';
  if (f !== undefined) {
    const g = new SvgPathMarks({ colors: true });
    drawFactionBadge(g, 'm', f.charge, f.color, 0, 0, HUB_FOUND.badgeDrawPx);
    const r = f2(Math.ceil(g.reach()));
    out = `<svg class="cf-badge" viewBox="-${r} -${r} ${2 * Number(r)} ${2 * Number(r)}"`
      + ` width="${HUB_FOUND.badgePx}" height="${HUB_FOUND.badgePx}" aria-hidden="true" focusable="false">`
      + `${g.markup()}</svg>`;
  }
  badges.set(faction, out);
  return out;
}

/** The SVG `stroke-dasharray` of one faction's edge dash, or '' for a
 *  solid line. The round cap makes a dash of 0.1 a dot, as on the map. */
function dashAttr(faction: number): string {
  const p = dashPattern(faction);
  return p.length === 0 ? '' : ` stroke-dasharray="${p.map(f2).join(' ')}"`;
}

/** The three passes of a territory border along the path `d`, in the
 *  faction ink: the two glow passes solid, the line pass with the dash. */
function borderPasses(d: string, faction: number): string {
  const ink = hex(factionColor(faction));
  const last = TERRITORY_BORDER_PASSES.length - 1;
  return TERRITORY_BORDER_PASSES.map((p, i) => `<path d="${d}" fill="none" stroke="${ink}"`
    + ` stroke-width="${f2(p.width)}" stroke-opacity="${f2(p.alpha)}" stroke-linejoin="round"`
    + ` stroke-linecap="round"${i === last ? dashAttr(faction) : ''}/>`).join('');
}

/** The border sample of a faction card: a straight run of the border, as
 *  long as `dashPx` world px, at zoom 1. */
function cardDashSvg(faction: number): string {
  const h = Math.ceil(TERRITORY_BORDER_PASSES[0].width) + 2;
  const w = HUB_FOUND.dashPx;
  const d = `M${f2(h / 2)} ${f2(h / 2)}H${f2(w - h / 2)}`;
  return `<svg class="cf-dash" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" aria-hidden="true"`
    + ` focusable="false">${borderPasses(d, faction)}</svg>`;
}

/**
 * The faction cards as one radio group. The picked card holds the one tab
 * stop; with no pick, the first card holds it. A press sends `FACTION_ACT`
 * with the faction id. The name of a card is its faction name, whole.
 */
function factionCards(picked: number): string {
  const stop = factionOf(picked) !== undefined ? picked : FACTIONS[0]?.id;
  return `<div class="cf-cards" role="radiogroup" aria-label="${esc(S.found_cards_label)}">`
    + FACTIONS.map((f) => {
      const on = f.id === picked;
      return `<button type="button" class="cf-card${on ? ' on' : ''}" role="radio" aria-checked="${on}"`
        + ` tabindex="${f.id === stop ? 0 : -1}" data-act="${FACTION_ACT}" data-id="${f.id}"`
        + ` style="--fk: ${channels(f.color).join(' ')}">`
        + `<span class="cf-pic">${cardBadgeSvg(f.id)}</span>`
        + `<span class="cf-txt"><span class="cf-name">${esc(factionName(f.id).toUpperCase())}</span>`
        + `<span class="cf-edge"><span class="cf-ink" aria-hidden="true"></span>${cardDashSvg(f.id)}</span></span>`
        + '<span class="cf-dot" aria-hidden="true"></span></button>';
    }).join('')
    + '</div>';
}

// --- the founding chart ---------------------------------------------------------

/** The cells within `ring` steps of `from`, added to `into`. */
function ringOf(from: number, ring: number, into: Set<number>): void {
  const { chartCols: cols, chartRows: rows } = HUB_FOUND;
  const dist = new Map<number, number>([[from, 0]]);
  const queue = [from];
  for (let q = 0; q < queue.length; q++) {
    const c = queue[q];
    into.add(c);
    const d = dist.get(c)!;
    if (d === ring) continue;
    for (let k = 0; k < 6; k++) {
      const n = neighborAt(c, k, cols, rows);
      if (n < 0 || dist.has(n)) continue;
      dist.set(n, d + 1);
      queue.push(n);
    }
  }
}

/**
 * The cells of the founding chart: the centre cell of the hold, and the
 * territory in index order. The territory is the hex ring round the hold
 * and a ring one step smaller round the cell `chartRing` steps east of it,
 * so the traced outline reads as a held patch of sea and not as the round
 * ring of a sonar.
 */
export function chartClaim(): { centre: number; claim: number[] } {
  const { chartCols: cols, chartRows: rows, chartRing: ring } = HUB_FOUND;
  const centre = Math.floor((rows - 1) / 2) * cols + Math.floor((cols - 1) / 2);
  const cells = new Set<number>();
  ringOf(centre, ring, cells);
  let lobe = centre;
  for (let i = 0; i < ring; i++) lobe = neighborAt(lobe, 0, cols, rows);
  if (lobe >= 0) ringOf(lobe, ring - 1, cells);
  return { centre, claim: [...cells].sort((a, b) => a - b) };
}

/** One corner of a cell, in world px. */
function corner(cell: number, k: number): [number, number] {
  const [ax, ay] = hexCorner(k % 6);
  return [cellCenterX(cell, HUB_FOUND.chartCols) + ax, cellCenterY(cell, HUB_FOUND.chartCols) + ay];
}

/** The lines of the uncharted water: every edge of the chart once. */
function waterPath(): string {
  const { chartCols: cols, chartRows: rows } = HUB_FOUND;
  let d = '';
  for (let c = 0; c < cols * rows; c++) {
    for (let k = 0; k < 6; k++) {
      const n = neighborAt(c, k, cols, rows);
      if (n >= 0 && n < c) continue;
      const [ax, ay] = corner(c, k);
      const [bx, by] = corner(c, k + 1);
      d += `M${f2(ax)} ${f2(ay)}L${f2(bx)} ${f2(by)}`;
    }
  }
  return d;
}

/**
 * The outline of the claim as closed paths: the traced outline of the map
 * (§4.2, R532), traced, relaxed and corner-cut by `outlineLoops` with its
 * own defaults, so the chart draws the border the map draws. The tint
 * fills the same path, and the dash runs along each whole loop, as the
 * outline lays it (R1577).
 */
export function claimLoops(claim: readonly number[]): string {
  const { chartCols: cols, chartRows: rows } = HUB_FOUND;
  const mask = new Uint8Array(cols * rows);
  for (const c of claim) mask[c] = 1;
  return outlineLoops(mask, 1, undefined, undefined, cols, rows)
    .map((loop) => loop.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${f2(x)} ${f2(y)}`).join('') + 'Z')
    .join('');
}

/** The hold at the centre and, for a picked faction, its badge beside it,
 *  as a hold of another faction wears it (§6.2 card 01). Faction 0 draws
 *  the hold in the ink of no faction. */
function holdMarks(centre: number, faction: number): string {
  const g = new SvgPathMarks({ colors: true });
  drawHoldMark(g, DEFAULT_HOLD_SYMBOL, 'm', factionColor(faction), 'other');
  const f = factionOf(faction);
  if (f !== undefined) {
    const [bx, by] = siteBadgeAt('m');
    drawFactionBadge(g, 'm', f.charge, f.color, bx, by, FACTION_MARKS.site.sizePx);
  }
  const x = f2(cellCenterX(centre, HUB_FOUND.chartCols));
  const y = f2(cellCenterY(centre, HUB_FOUND.chartCols));
  return `<g transform="translate(${x} ${y})">${g.markup()}</g>`;
}

/** The founding chart of each pick, built once: the hub draws the pane
 *  again every second, and the chart is the same picture each time. */
const charts = new Map<number, string>();

/**
 * The founding chart as one SVG, named for a screen reader. With no pick it
 * is the uncharted water and a hold that no faction holds yet.
 */
function foundingChartSvg(faction: number): string {
  const hit = charts.get(faction);
  if (hit !== undefined) return hit;
  const out = buildChart(faction);
  charts.set(faction, out);
  return out;
}

function buildChart(faction: number): string {
  const { chartCols: cols, chartRows: rows, chartZoom: zoom } = HUB_FOUND;
  const picked = factionOf(faction) !== undefined;
  const w = COL_PITCH * (cols + 0.5);
  const h = cellCenterY((rows - 1) * cols, cols) + HEX_SIZE;
  const { centre, claim } = chartClaim();
  const name = picked ? S.found_chart_alt({ name: factionName(faction).toUpperCase() }) : S.found_chart_alt_none;
  const outline = claimLoops(claim);
  const territory = picked
    ? `<path d="${outline}" fill="${hex(factionColor(faction))}" fill-opacity="${f2(TERRITORY_TINT_ALPHA)}"/>`
      + borderPasses(outline, faction)
    : '';
  return `<svg class="cf-map" viewBox="0 0 ${f2(w)} ${f2(h)}" width="${f2(w * zoom)}" height="${f2(h * zoom)}"`
    + ` role="img" aria-label="${esc(name)}">`
    + `<path class="cf-water" d="${waterPath()}"/>${territory}${holdMarks(centre, picked ? faction : 0)}</svg>`;
}

/** The founding chart with its head and its line. */
function foundingChart(faction: number): string {
  const picked = factionOf(faction) !== undefined;
  return `<div class="cf-chart"><h3 class="o-sec"><b>${esc(S.found_chart_head)}</b></h3>`
    + foundingChartSvg(faction)
    + `${lede(picked ? S.found_chart_line : S.found_chart_none)}</div>`;
}

// --- the form --------------------------------------------------------------------

/**
 * The founding form of START ONE: the warning that the banner is fixed, the
 * faction cards, and beside them (under them on a narrow pane) the founding
 * chart over the form. `form` is the panel's markup of the name box, the
 * tag box, FOUND IT and its gate line.
 */
export function foundingBody(picked: number, form: string): string {
  return `<div class="cf"><div class="lede cf-warn">${esc(S.create_lede_banner)}</div>`
    + `<div class="cf-row">${factionCards(picked)}<div class="cf-side">${foundingChart(picked)}`
    + `<div class="cf-form"><h3 class="o-sec"><b>${esc(S.found_form_head)}</b></h3>${form}</div></div></div></div>`;
}

// --- the key walk ------------------------------------------------------------------

/**
 * The walk keys of `[keys.hub]` on the radio group of the faction cards
 * (R2155): `←` `↑` and `→` `↓` move the pick to the card before or after
 * it, round the ends, and `home` `end` to the first or the last card. The
 * focus moves to the new card first, so the hub's `keepFocus` gives it
 * back to that card after the rewrite. `pick` takes the faction id.
 */
export function wireFactionWalk(root: HTMLElement, pick: (faction: number) => void): void {
  const group = root.querySelector<HTMLElement>('.cf-cards[role="radiogroup"]');
  if (group === null || !wireOnce(group)) return;
  group.addEventListener('keydown', (ev) => {
    const move = matchKey(ev, KEYS.hub);
    // The walk answers the `walk` rows alone. Enter presses the focused
    // card itself; the `field` rows (`submit`, `cancel`) belong to a text field.
    if (move !== 'prev' && move !== 'next' && move !== 'first' && move !== 'last') return;
    const cards = [...group.querySelectorAll<HTMLElement>(`[data-act="${FACTION_ACT}"]`)];
    if (cards.length === 0) return;
    const at = cards.findIndex((c) => c === document.activeElement);
    const from = at < 0 ? cards.findIndex((c) => c.getAttribute('aria-checked') === 'true') : at;
    const n = cards.length;
    const to = move === 'first' ? 0 : move === 'last' ? n - 1
      : (Math.max(from, 0) + (move === 'prev' ? -1 : 1) + n) % n;
    ev.preventDefault();
    cards[to].focus();
    pick(Number.parseInt(cards[to].dataset.id ?? '0', 10));
  });
}
